/**
 * Alchemy NFT API — owned token IDs for a contract (Ethereum mainnet).
 * @see https://docs.alchemy.com/reference/getnftsforowner-v3
 */

/** Short TTL cache: dashboard refresh + inventory re-entry were each doing full paginated Alchemy runs. */
const OWNED_IDS_CACHE_MS = 45_000;

/** @type {Map<string, { t: number, ids: string[] }>} */
const ownedIdsCache = new Map();

/**
 * Drop cached owned-token lists (e.g. after stake/unstake or wallet switch).
 * @param {string | null} [owner] If set, only entries for this wallet are removed.
 */
export function clearOwnedNftCache(owner = null) {
  if (owner == null || owner === "") {
    ownedIdsCache.clear();
    return;
  }
  const p = owner.toLowerCase() + ":";
  for (const k of ownedIdsCache.keys()) {
    if (k.startsWith(p)) ownedIdsCache.delete(k);
  }
}

/**
 * @param {string} tokenId
 * @returns {string} decimal string for URLs
 */
export function normalizeTokenId(tokenId) {
  const s = String(tokenId).trim();
  if (!s) return s;
  if (/^0x[0-9a-fA-F]+$/.test(s)) return BigInt(s).toString(10);
  return s;
}

/**
 * @param {string} owner
 * @param {string} contractAddress
 * @param {string} apiKey
 * @returns {Promise<string[]>} sorted numeric token id strings
 */
export async function fetchOwnedTokenIds(owner, contractAddress, apiKey) {
  const cacheKey = `${owner.toLowerCase()}:${contractAddress.toLowerCase()}`;
  const hit = ownedIdsCache.get(cacheKey);
  if (hit && Date.now() - hit.t < OWNED_IDS_CACHE_MS) {
    return hit.ids;
  }

  const base = `https://eth-mainnet.g.alchemy.com/nft/v3/${apiKey}/getNFTsForOwner`;
  const ids = [];
  let pageKey;

  do {
    const params = new URLSearchParams();
    params.set("owner", owner);
    params.append("contractAddresses[]", contractAddress);
    params.set("withMetadata", "false");
    if (pageKey) params.set("pageKey", pageKey);

    const url = `${base}?${params.toString()}`;
    const res = await fetch(url);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Alchemy ${res.status}: ${text.slice(0, 200)}`);
    }
    const data = await res.json();
    const list = data.ownedNfts ?? [];
    for (const n of list) {
      const raw = n.tokenId ?? n.id?.tokenId;
      if (raw != null) ids.push(normalizeTokenId(raw));
    }
    pageKey = data.pageKey;
  } while (pageKey);

  const unique = [...new Set(ids)];
  unique.sort((a, b) => {
    try {
      const x = BigInt(a);
      const y = BigInt(b);
      return x < y ? -1 : x > y ? 1 : 0;
    } catch {
      return a.localeCompare(b);
    }
  });
  ownedIdsCache.set(cacheKey, { t: Date.now(), ids: unique });
  return unique;
}
